import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  Popconfirm,
  Tag,
  Space,
  Badge,
  MessagePlugin
} from 'tdesign-react';
import { CheckIcon, DeleteIcon, BrowseIcon, UserIcon } from 'tdesign-icons-react';
import { marketingFormApi } from '../../api';
import type { MarketingForm } from '../../types';

const statusOptions = [
  { label: '待处理', value: 0, theme: 'warning' },
  { label: '已联系', value: 1, theme: 'primary' },
  { label: '已成交', value: 2, theme: 'success' },
  { label: '已关闭', value: 3, theme: 'default' },
];

export default function MarketingManage() {
  const [data, setData] = useState<MarketingForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [dialogVisible, setDialogVisible] = useState(false);
  const [handleDialogVisible, setHandleDialogVisible] = useState(false);
  const [editingForm, setEditingForm] = useState<MarketingForm | null>(null);
  const [handleStatus, setHandleStatus] = useState(1);
  const [handleRemark, setHandleRemark] = useState('');
  const [formTypes, setFormTypes] = useState<string[]>([]);
  const [pendingCount, setPendingCount] = useState(0);

  const columns = [
    { colKey: 'id', title: 'ID', width: 80 },
    { colKey: 'formType', title: '表单类型', width: 120 },
    { colKey: 'contactName', title: '联系人', width: 100 },
    { colKey: 'contactPhone', title: '联系电话', width: 140 },
    { colKey: 'contactEmail', title: '邮箱', width: 180, ellipsis: true },
    { colKey: 'companyName', title: '公司名称', ellipsis: true },
    { colKey: 'sourcePage', title: '来源页面', width: 150 },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => {
        const status = statusOptions.find(s => s.value === row.status);
        return <Tag theme={status?.theme as any}>{status?.label || '未知'}</Tag>;
      },
    },
    { colKey: 'handlerName', title: '处理人', width: 100 },
    { colKey: 'createTime', title: '提交时间', width: 180 },
    {
      colKey: 'action',
      title: '操作',
      width: 200,
      cell: ({ row }: any) => (
        <Space>
          {row.status === 0 && (
            <Button
              theme="primary"
              variant="text"
              icon={<CheckIcon />}
              onClick={() => handleOpenHandle(row)}
            >
              处理
            </Button>
          )}
          <Button
            theme="primary"
            variant="text"
            icon={<BrowseIcon />}
            onClick={() => handleView(row)}
          >
            查看
          </Button>
          <Popconfirm
            content="确定要删除该记录吗？"
            onConfirm={() => handleDelete(row.id)}
          >
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const res = await marketingFormApi.getList({ pageNum: page, pageSize });
      if (res.code === 200) {
        setData(res.data.list);
        setPagination({
          current: res.data.pageNum,
          pageSize: res.data.pageSize,
          total: res.data.total,
        });
      }
    } catch (err) {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormTypes = async () => {
    try {
      const res = await marketingFormApi.getTypes();
      if (res.code === 200) {
        setFormTypes(res.data);
      }
    } catch (err) {
      console.error('加载表单类型失败:', err);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const res = await marketingFormApi.countPending();
      if (res.code === 200) {
        setPendingCount(res.data);
      }
    } catch (err) {
      console.error('加载待处理数量失败:', err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchFormTypes();
    fetchPendingCount();
  }, []);

  const handleView = (form: MarketingForm) => {
    setEditingForm(form);
    setDialogVisible(true);
  };

  const handleOpenHandle = (form: MarketingForm) => {
    setEditingForm(form);
    setHandleStatus(1);
    setHandleRemark('');
    setHandleDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!editingForm) return;

    try {
      const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}');
      const res = await marketingFormApi.handle(
        editingForm.id,
        handleStatus,
        handleRemark,
        adminInfo.id,
        adminInfo.username
      );

      if (res.code === 200) {
        MessagePlugin.success('处理成功');
        setHandleDialogVisible(false);
        fetchData();
        fetchPendingCount();
      } else {
        MessagePlugin.error(res.message || '处理失败');
      }
    } catch (err) {
      MessagePlugin.error('处理失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await marketingFormApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
        fetchPendingCount();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch (err) {
      MessagePlugin.error('删除失败');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">营销中心</h1>
          {pendingCount > 0 && (
            <Badge count={pendingCount}>
              <span className="text-gray-500">待处理</span>
            </Badge>
          )}
        </div>
      </div>

      <Table
        data={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (pageInfo: any) => fetchData(pageInfo.current, pageInfo.pageSize),
        }}
      />

      {/* 查看详情对话框 */}
      <Dialog
        header="表单详情"
        visible={dialogVisible}
        onClose={() => setDialogVisible(false)}
        footer={null}
        width={500}
      >
        {editingForm && (
          <div className="py-4 space-y-4">
            <div className="flex">
              <span className="w-24 text-gray-500">表单类型：</span>
              <span>{editingForm.formType || '-'}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-gray-500">联系人：</span>
              <span>{editingForm.contactName}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-gray-500">联系电话：</span>
              <span>{editingForm.contactPhone}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-gray-500">邮箱：</span>
              <span>{editingForm.contactEmail || '-'}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-gray-500">公司名称：</span>
              <span>{editingForm.companyName || '-'}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-gray-500">来源页面：</span>
              <span>{editingForm.sourcePage || '-'}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-gray-500">备注：</span>
              <span>{editingForm.remark || '-'}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-gray-500">状态：</span>
              <Tag theme={statusOptions.find(s => s.value === editingForm.status)?.theme as any}>
                {statusOptions.find(s => s.value === editingForm.status)?.label}
              </Tag>
            </div>
            {editingForm.handlerName && (
              <>
                <div className="flex">
                  <span className="w-24 text-gray-500">处理人：</span>
                  <span>{editingForm.handlerName}</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-gray-500">处理备注：</span>
                  <span>{editingForm.handleRemark || '-'}</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-gray-500">处理时间：</span>
                  <span>{editingForm.handleTime || '-'}</span>
                </div>
              </>
            )}
          </div>
        )}
      </Dialog>

      {/* 处理对话框 */}
      <Dialog
        header="处理表单"
        visible={handleDialogVisible}
        onClose={() => setHandleDialogVisible(false)}
        onConfirm={handleSubmit}
      >
        <div className="py-4 space-y-4">
          <div>
            <div className="mb-2">处理状态：</div>
            <Select
              value={handleStatus}
              onChange={(value) => setHandleStatus(value as number)}
              options={statusOptions.filter(s => s.value !== 0).map(s => ({ label: s.label, value: s.value }))}
            />
          </div>
          <div>
            <div className="mb-2">处理备注：</div>
            <Input
              value={handleRemark}
              onChange={(value) => setHandleRemark(value)}
              placeholder="请输入处理备注（可选）"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
